import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import _isNil from 'lodash-es/isNil';
import _find from 'lodash-es/find';
import { environment } from 'src/environments/environment';
import _isArray from 'lodash-es/isArray';
import { TravelAdvisorPositionEnum, UserRolesEnum } from '../shared/models/user-roles-enum';
import each from 'lodash-es/each';
import isUndefined from 'lodash-es/isUndefined';
import { UserAppAccess } from '../shared/models/user-app-access.interface';

export type onAccountActivateFunc = (arg: any) => void;

@Injectable({
  providedIn: 'root'
})
export class AuthHandlerService {
  private roleClaim = 'extension_Role';
  private positionClaim = 'extension_Position';
  private hasAcceptedInviteClaim = 'extension_HasAcceptedInvite';
  private vendorIdClaim = 'extension_VendorId';
  private _activeAccountInfo$: Observable<AccountInfo | null>;
  private isAuthenticated = false;
	private _baseApiUrl: string;
	private _onAccountActivate: onAccountActivateFunc[] = [];
  public get activeAccountInfo$() {
    return this._activeAccountInfo$ || (this._activeAccountInfo$ = this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        map((_: InteractionStatus) => {
          this.checkAndSetActiveAccount();
          const activeAccount = this.authService.instance.getActiveAccount();
          //console.log('auth-handler.service.activeAccountInfo.activeAccount:', activeAccount);
          return activeAccount;
        })
      ));
  }


  public get activeAccountRole$(): Observable<UserRolesEnum> {   
    const activeRole = this._activeAccountInfo$.pipe(map(this.getAccountRole));
    //console.log('auth-handler.service.activeAccountRole.activeRole:', activeRole);
    return activeRole;
  }   
  
  public get activeAccountTravelAdvisorPosition$(): Observable<TravelAdvisorPositionEnum> {   
    const activePosition = this._activeAccountInfo$.pipe(map(this.getAccountTravelAdvisorPosition));
    //console.log('auth-handler.service._activeAccountInfo.activePosition:', activePosition);
    return activePosition;
  }  

  public get activeAccountVendorId$(): Observable<string> {   
    return this._activeAccountInfo$.pipe(map(this.getAccountVendorId));
  }  

  constructor(private msalBroadcastService: MsalBroadcastService,
		private authService: MsalService) {}


  public setOnAccountActivate(func: onAccountActivateFunc) {
    this._onAccountActivate.push(func);
  }
    
  public validateUserRole(roles: string | string[]): boolean {
    if (!_isArray(roles)) {
      roles = [roles as string];
    }

    return !!_find(roles, r=> this.activeAccountRole() === r);
  }

  public activeAccount() {    
    return this.authService.instance.getActiveAccount();
  }

  public activeAccountRole() {
    const account = this.authService.instance.getActiveAccount();
    return this.getAccountRole(account);
  }

  public activeAccountVendorId() {
    const account = this.authService.instance.getActiveAccount();
    return this.getAccountVendorId(account);
  }

  private getAccountRole = (account: AccountInfo | null) => {
    return this.getAccountClaimBuilder(account, UserRolesEnum.anon)(this.roleClaim);
  }

  private getAccountTravelAdvisorPosition = (account: AccountInfo | null) => {
    const defaultPosition = (account && account?.idTokenClaims['extension_Role'] == UserRolesEnum.travelAdvisor) ? TravelAdvisorPositionEnum.independentConsultant : null,
      position = this.getAccountClaimBuilder(account, defaultPosition)(this.positionClaim);
    //console.log('auth-handler.service.getAccountTravelAdvisorPosition.position:', position);
    if (position === TravelAdvisorPositionEnum.other) {
      return TravelAdvisorPositionEnum.independentConsultant;
    }
    return position;
  }

  public getAccountHasAcceptedInvite = (account: AccountInfo | null) => {
    return this.getAccountClaimBuilder(account, true)(this.hasAcceptedInviteClaim);
  }

  private getAccountVendorId = (account: AccountInfo | null) => {
    return this.getAccountClaimBuilder(account, null)(this.vendorIdClaim);
  }

  private getAccountClaimBuilder<T>(account: AccountInfo | null, defaultValue: T): (claim: string) => T {
    return (claim: string) => account && account.idTokenClaims && !isUndefined(account.idTokenClaims[claim]) ? account.idTokenClaims[claim] as T : defaultValue;
  }

  public logoutIfInvalidAccount() {
    const account = this.authService.instance.getActiveAccount();
    if (account && account.environment !== environment.authEnv) {
      localStorage && localStorage.clear();
      sessionStorage && sessionStorage.clear();
    }
  }

	private checkAndSetActiveAccount(){
		let activeAccount = this.authService.instance.getActiveAccount();
		if ((!activeAccount || activeAccount.environment !== environment.authEnv) && this.authService.instance.getAllAccounts().length > 0) {
			const accounts = this.authService.instance.getAllAccounts();
      const account = _find(accounts, x=> x.environment === environment.authEnv);
			this.authService.instance.setActiveAccount(account);
      this.isAuthenticated = true;
      each(this._onAccountActivate, (func) => func(account));

			return account;
		}

		return null;
	}


  /**
   * Check to see what level of access a user has by role and position
   * @returns 
  */
  public getUserAppAccess(){
    const activeAccount = this.activeAccount(),
      isLoggedIn = activeAccount ? true : false,
      isAdmin = this.validateUserRole([UserRolesEnum.admin, UserRolesEnum.developer]),
      isTravelAdvisorRole = activeAccount?.idTokenClaims['extension_Role'] == UserRolesEnum.travelAdvisor ? true : false,
      isTravelAdvisorAdmin = (activeAccount?.idTokenClaims['extension_Position'] == TravelAdvisorPositionEnum.agencyOwner || activeAccount?.idTokenClaims['extension_Position'] == TravelAdvisorPositionEnum.agencyAdmin) ? true : false,
      userAppAccess: UserAppAccess = {
        activeAccount: activeAccount, 
        isLoggedIn: isLoggedIn, 
        isAdmin: isAdmin, 
        isTravelAdvisorRole: isTravelAdvisorRole,
        isTravelAdvisorAdmin: isTravelAdvisorAdmin
      };

    // console.group('getUserAppAccess()')
    // console.log('activeAccount: ', activeAccount);
    // //console.log('isLoggedIn: ', isLoggedIn);
    // //console.log('isAdmin: ', isAdmin);
    // //console.log('isTravelAdvisorRole: ', isTravelAdvisorRole);
    // console.log('extension_Position: ', activeAccount?.idTokenClaims['extension_Position']);
    // //console.log('isTravelAdvisorAdmin: ', isTravelAdvisorAdmin);
    // console.log('userAppAccess: ', userAppAccess);
    // console.groupEnd();
    return userAppAccess;
  }
}
